/**
 * useIsMobile — reactive mobile-viewport check.
 *
 * Keeps the breakpoint defined here as the single source of truth so every
 * caller agrees on what "mobile" means.
 *
 * Reach for this hook when the breakpoint has to drive *logic* — choosing an
 * initial state, switching layouts, or any branch JS/TS must make at runtime.
 * When the responsive change is purely visual, prefer a CSS `@media` rule and
 * use this hook only for the cases CSS alone can't express.
 */

import { useEffect, useState } from 'react';
// FLO-523: the pristine matchMedia — the sidebar-compat viewport facade rewrites
// `(max-width: …)` queries for the HOST page, and a 1100px desktop with a 400px
// rail docked would otherwise report itself as mobile to our own layout. No
// behavioural difference when the facade is off (see ../sdk/native-viewport).
import { hasMatchMedia, realMatchMedia } from '../sdk/native-viewport';

/**
 * Viewport width (px) at/below which the viewport is treated as mobile.
 * 768px is the conventional tablet/phone boundary.
 */
export const MOBILE_BREAKPOINT_PX = 768;

/** The media query string derived from {@link MOBILE_BREAKPOINT_PX}. */
export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT_PX}px)`;

/**
 * Synchronous, one-shot read of the current match. Safe to call during
 * render — returns `false` when there's no `window`/`matchMedia` (SSR or a
 * non-DOM test env) so callers get a sensible "desktop" default. Also used
 * as the hook's lazy initial state so the first paint is already correct.
 */
export function matchesMobile(): boolean {
  if (!hasMatchMedia()) return false;
  return !!realMatchMedia(MOBILE_MEDIA_QUERY)?.matches;
}

/**
 * Returns `true` while the viewport is ≤ {@link MOBILE_BREAKPOINT_PX} and
 * updates live as the viewport crosses the breakpoint (resize / rotate).
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(matchesMobile);

  useEffect(() => {
    const mql = realMatchMedia(MOBILE_MEDIA_QUERY);
    if (!mql) return;
    const onChange = () => setIsMobile(mql.matches);
    // Re-sync once on mount: the viewport may have changed between the
    // initial render and this effect running.
    onChange();
    // `addEventListener` is the modern API; `addListener` is the deprecated
    // fallback for older Safari (< 14) where the former is missing.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isMobile;
}
